import React, { useState, useEffect, useCallback } from 'react';
import {
  FiGlobe, FiRefreshCw, FiSave, FiPlus, FiTrash2,
  FiCheck, FiAlertCircle, FiCloud, FiEdit2, FiX,
  FiChevronUp, FiChevronDown,
} from 'react-icons/fi';
import { getPlatformConfig, updatePlatformConfig, syncAwsRegions } from '../../api/admin';
import type { AwsRegion } from '../../api/admin';

/* ── Types ────────────────────────────────────────────────────────────────── */
type CloudTab = 'aws_regions' | 'azure_regions' | 'gcp_regions';

const GROUPS = ['Europe', 'United States', 'Canada', 'South America', 'Asia Pacific', 'Middle East & Africa', 'Other'];

const emptyRegion = (defaultGroup = 'Europe'): AwsRegion => ({ code: '', label: '', group: defaultGroup });

/* ── RegionsEditor (générique AWS / Azure / GCP) ─────────────────────────── */
interface RegionsEditorProps {
  configKey: CloudTab;
  canSync?: boolean;
  accentColor: string;  // tailwind color name: orange | blue | green
}

const RegionsEditor: React.FC<RegionsEditorProps> = ({ configKey, canSync = false, accentColor }) => {
  const [regions,     setRegions]     = useState<AwsRegion[]>([]);
  const [updatedAt,   setUpdatedAt]   = useState<string | null>(null);
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [syncing,     setSyncing]     = useState(false);
  const [toast,       setToast]       = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const [filter,      setFilter]      = useState('');
  const [showAddRow,  setShowAddRow]  = useState(false);
  const [newRow,      setNewRow]      = useState<AwsRegion>(emptyRegion());
  // Édition inline
  const [editingIdx,  setEditingIdx]  = useState<number | null>(null);
  const [editBuf,     setEditBuf]     = useState<AwsRegion | null>(null);

  const accent = {
    ring:    `focus:ring-${accentColor}-400`,
    btn:     `border-${accentColor}-300 bg-${accentColor}-50 text-${accentColor}-700 hover:bg-${accentColor}-100`,
    tab:     `text-${accentColor}-600`,
    mono:    `text-${accentColor}-600`,
    syncBtn: `border-${accentColor}-300 bg-${accentColor}-50 text-${accentColor}-700 hover:bg-${accentColor}-100`,
  };

  const showToast = (type: 'success' | 'error', msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getPlatformConfig(configKey);
      setRegions(data.value as AwsRegion[]);
      setUpdatedAt(data.updated_at);
    } catch {
      showToast('error', 'Impossible de charger la configuration');
    } finally {
      setLoading(false);
    }
  }, [configKey]);

  useEffect(() => { load(); setFilter(''); setEditingIdx(null); setShowAddRow(false); }, [load]);

  /* ── Actions ── */
  const handleSave = async () => {
    setSaving(true);
    try {
      await updatePlatformConfig(configKey, regions);
      showToast('success', `${regions.length} régions sauvegardées`);
      load();
    } catch { showToast('error', 'Erreur lors de la sauvegarde'); }
    finally  { setSaving(false); }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await syncAwsRegions();
      setRegions(result.regions);
      showToast('success', `${result.count} régions synchronisées depuis AWS`);
      load();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || 'Erreur de synchronisation AWS';
      showToast('error', msg);
    } finally { setSyncing(false); }
  };

  const handleDelete = (index: number) => {
    setRegions(prev => prev.filter((_, i) => i !== index));
    if (editingIdx === index) setEditingIdx(null);
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    setRegions(prev => {
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next;
    });
    setEditingIdx(null);
  };

  const handleMoveDown = (index: number) => {
    setRegions(prev => {
      if (index >= prev.length - 1) return prev;
      const next = [...prev];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return next;
    });
    setEditingIdx(null);
  };

  const startEdit = (index: number) => {
    setEditingIdx(index);
    setEditBuf({ ...regions[index] });
  };

  const commitEdit = () => {
    if (editingIdx === null || !editBuf) return;
    if (!editBuf.code.trim() || !editBuf.label.trim()) return;
    setRegions(prev => prev.map((r, i) => i === editingIdx ? { ...editBuf } : r));
    setEditingIdx(null);
    setEditBuf(null);
  };

  const cancelEdit = () => { setEditingIdx(null); setEditBuf(null); };

  const handleAddRow = () => {
    if (!newRow.code.trim() || !newRow.label.trim()) return;
    setRegions(prev => [...prev, { ...newRow }]);
    setNewRow(emptyRegion());
    setShowAddRow(false);
  };

  /* ── Filtered + grouped ── */
  const filtered = filter
    ? regions.filter(r =>
        r.code.toLowerCase().includes(filter.toLowerCase()) ||
        r.label.toLowerCase().includes(filter.toLowerCase()) ||
        r.group.toLowerCase().includes(filter.toLowerCase())
      )
    : regions;

  const byGroup = GROUPS.reduce<Record<string, { region: AwsRegion; realIndex: number }[]>>((acc, g) => {
    acc[g] = filtered
      .map((r, i) => ({ region: r, realIndex: regions.indexOf(r) }))
      .filter(({ region }) => region.group === g);
    return acc;
  }, {});

  return (
    <div className="space-y-4">

      {/* ── Toolbar ── */}
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-gray-500">
          {updatedAt && (
            <span className="text-gray-400">Mise à jour : {new Date(updatedAt).toLocaleString('fr-FR')}</span>
          )}
        </p>
        <div className="flex items-center gap-2 flex-shrink-0">
          {canSync && (
            <button onClick={handleSync} disabled={syncing || saving}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border disabled:opacity-50 text-sm font-medium transition-colors ${accent.syncBtn}`}
              title="Synchronise depuis l'API AWS DescribeRegions">
              <FiCloud className={syncing ? 'animate-pulse' : ''} />
              {syncing ? 'Sync…' : 'Sync depuis AWS'}
            </button>
          )}
          <button onClick={handleSave} disabled={saving || syncing}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-green-300 bg-green-50 text-green-700 hover:bg-green-100 disabled:opacity-50 text-sm font-medium transition-colors">
            <FiSave className={saving ? 'animate-pulse' : ''} />
            {saving ? 'Sauvegarde…' : 'Sauvegarder'}
          </button>
        </div>
      </div>

      {/* ── Toast ── */}
      {toast && (
        <div className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium border ${
          toast.type === 'success' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'
        }`}>
          {toast.type === 'success' ? <FiCheck /> : <FiAlertCircle />} {toast.msg}
        </div>
      )}

      {/* ── Filter + count + add ── */}
      <div className="flex items-center gap-3">
        <input
          type="text" value={filter} onChange={e => setFilter(e.target.value)}
          placeholder="Filtrer par code, label ou groupe…"
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
        />
        <span className="text-sm text-gray-500 flex-shrink-0">{filtered.length} / {regions.length}</span>
        <button onClick={() => setShowAddRow(v => !v)}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors">
          <FiPlus /> Ajouter
        </button>
      </div>

      {/* ── Add row form ── */}
      {showAddRow && (
        <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <input type="text" placeholder="Code" value={newRow.code}
            onChange={e => setNewRow(r => ({ ...r, code: e.target.value }))}
            className="w-40 border border-gray-300 rounded px-2 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-gray-300" />
          <input type="text" placeholder="Label" value={newRow.label}
            onChange={e => setNewRow(r => ({ ...r, label: e.target.value }))}
            className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300" />
          <select value={newRow.group} onChange={e => setNewRow(r => ({ ...r, group: e.target.value }))}
            className="w-44 border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300">
            {GROUPS.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
          <button onClick={handleAddRow} disabled={!newRow.code.trim() || !newRow.label.trim()}
            className="px-3 py-1.5 rounded border border-green-300 bg-green-50 text-green-700 hover:bg-green-100 disabled:opacity-40 text-sm font-medium transition-colors">
            Ajouter
          </button>
          <button onClick={() => setShowAddRow(false)} className="text-gray-400 hover:text-gray-600">
            <FiX size={16} />
          </button>
        </div>
      )}

      {/* ── Table ── */}
      {loading ? (
        <div className="flex items-center gap-2 text-gray-400 py-12 justify-center">
          <FiRefreshCw className="animate-spin" /> Chargement…
        </div>
      ) : (
        <div className="space-y-3">
          {GROUPS.map(group => {
            const rows = byGroup[group];
            if (!rows || rows.length === 0) return null;
            return (
              <div key={group} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">{group}</span>
                  <span className="text-xs text-gray-400">({rows.length})</span>
                </div>
                <table className="w-full text-sm">
                  <tbody>
                    {rows.map(({ region, realIndex }) => {
                      const isEditing = editingIdx === realIndex;
                      return (
                        <tr key={`${region.code}-${realIndex}`}
                          className={`group border-b border-gray-100 last:border-0 transition-colors ${isEditing ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>

                          {isEditing && editBuf ? (
                            <>
                              <td className="px-3 py-1.5 w-44">
                                <input type="text" value={editBuf.code}
                                  onChange={e => setEditBuf(b => b ? { ...b, code: e.target.value } : b)}
                                  className="w-full border border-blue-300 rounded px-2 py-1 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-400"
                                />
                              </td>
                              <td className="px-3 py-1.5">
                                <input type="text" value={editBuf.label}
                                  onChange={e => setEditBuf(b => b ? { ...b, label: e.target.value } : b)}
                                  className="w-full border border-blue-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                                />
                              </td>
                              <td className="px-3 py-1.5 w-44">
                                <select value={editBuf.group}
                                  onChange={e => setEditBuf(b => b ? { ...b, group: e.target.value } : b)}
                                  className="w-full border border-blue-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400">
                                  {GROUPS.map(g => <option key={g} value={g}>{g}</option>)}
                                </select>
                              </td>
                              <td className="px-3 py-1.5 text-right w-24">
                                <div className="flex items-center justify-end gap-1">
                                  <button onClick={commitEdit}
                                    className="text-green-600 hover:text-green-800 p-1" title="Valider">
                                    <FiCheck size={14} />
                                  </button>
                                  <button onClick={cancelEdit}
                                    className="text-gray-400 hover:text-gray-600 p-1" title="Annuler">
                                    <FiX size={14} />
                                  </button>
                                </div>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className={`px-4 py-2.5 w-44 font-mono text-xs font-medium ${accentColor === 'orange' ? 'text-orange-600' : accentColor === 'blue' ? 'text-blue-600' : 'text-green-600'}`}>
                                {region.code}
                              </td>
                              <td className="px-4 py-2.5 text-gray-700">{region.label}</td>
                              <td className="px-4 py-2.5 text-right w-28">
                                <div className="flex items-center justify-end gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <div className="flex items-center gap-0.5">
                                    <button onClick={() => handleMoveUp(realIndex)}
                                      className="text-gray-300 hover:text-gray-600 p-1 transition-colors" title="Monter">
                                      <FiChevronUp size={13} />
                                    </button>
                                    <button onClick={() => handleMoveDown(realIndex)}
                                      className="text-gray-300 hover:text-gray-600 p-1 transition-colors" title="Descendre">
                                      <FiChevronDown size={13} />
                                    </button>
                                    <button onClick={() => startEdit(realIndex)}
                                      className="text-gray-300 hover:text-blue-500 p-1 transition-colors" title="Modifier">
                                      <FiEdit2 size={13} />
                                    </button>
                                    <button onClick={() => handleDelete(realIndex)}
                                      className="text-gray-300 hover:text-red-500 p-1 transition-colors" title="Supprimer">
                                      <FiTrash2 size={13} />
                                    </button>
                                  </div>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}

      {/* ── AWS sync info ── */}
      {canSync && (
        <div className="p-3 bg-orange-50 rounded-lg border border-orange-200 text-xs text-gray-600">
          <strong className="text-gray-700">Sync depuis AWS</strong> — Utilise l'API{' '}
          <code className="text-orange-600 font-mono">DescribeRegions</code>. Nécessite{' '}
          <code className="text-orange-600 font-mono">AWS_ACCESS_KEY_ID</code> et{' '}
          <code className="text-orange-600 font-mono">AWS_SECRET_ACCESS_KEY</code> dans les variables d'environnement du backend.
        </div>
      )}
    </div>
  );
};

/* ── Page principale ──────────────────────────────────────────────────────── */
const TABS: { id: CloudTab; label: string; icon: React.ReactNode; color: string; accentColor: string }[] = [
  { id: 'aws_regions',   label: 'Régions AWS',   icon: <FiCloud />,  color: 'text-orange-600', accentColor: 'orange' },
  { id: 'azure_regions', label: 'Régions Azure', icon: <FiGlobe />,  color: 'text-blue-600',   accentColor: 'blue'   },
  { id: 'gcp_regions',   label: 'Régions GCP',   icon: <FiGlobe />,  color: 'text-green-600',  accentColor: 'green'  },
];

const AdminPlatformConfig: React.FC = () => {
  const [activeTab, setActiveTab] = useState<CloudTab>('aws_regions');

  return (
    <div className="p-6 space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Configuration Plateforme</h1>
        <p className="text-sm text-gray-500 mt-1">
          Paramètres dynamiques modifiables sans redéploiement — régions cloud disponibles.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab.id
                ? `${tab.color} border-current`
                : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
            }`}>
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div>
        {TABS.map(tab => (
          activeTab === tab.id && (
            <RegionsEditor
              key={tab.id}
              configKey={tab.id}
              canSync={tab.id === 'aws_regions'}
              accentColor={tab.accentColor}
            />
          )
        ))}
      </div>
    </div>
  );
};

export default AdminPlatformConfig;
